import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo
} from 'react';
import axios from '../axiosConfig';
import {
    GRACE_PERIOD_DAYS,
    API_ENDPOINTS,
    SUBSCRIPTION_STATUS,
    USER_TYPE
} from '../utils/constants';
import { formatDate, getSubscriptionStatusText } from '../utils/formatters';

// Create context
const SubscriptionContext = createContext(null);

/**
 * Hook to access subscription context
 * @throws {Error} If used outside of SubscriptionProvider
 */
export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

/**
 * SubscriptionProvider Component
 * Manages subscription state and provides it to the app
 */
export const SubscriptionProvider = ({ children }) => {
    // State
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userType, setUserType] = useState(null);

    // Refs
    const mountedRef = useRef(true);
    const hasFetchedRef = useRef(false);

    // ==================== Helper Functions ====================

    /**
     * Check authentication status from localStorage
     */
    const checkAuthStatus = useCallback(() => {
        const token = localStorage.getItem('jwt');
        const storedUserType = localStorage.getItem('userType');
        return {
            isAuthenticated: !!token,
            userType: storedUserType
        };
    }, []);

    /**
     * Calculate grace period data for a subscription
     */
    const calculateGracePeriod = useCallback((data) => {
        if (!data.endDate || data.isActive) {
            return {
                ...data,
                hasAccess: data.isActive,
                isInGracePeriod: data.isInGracePeriod || false
            };
        }

        const endDate = new Date(data.endDate);
        const now = new Date();
        const gracePeriodEnd = new Date(endDate);
        gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);

        const isExpired = now > endDate;
        const withinGracePeriod = now < gracePeriodEnd;
        const isInGracePeriod = isExpired && withinGracePeriod;

        return {
            ...data,
            isInGracePeriod,
            gracePeriodEndDate: gracePeriodEnd.toISOString(),
            hasAccess: data.isActive || isInGracePeriod
        };
    }, []);

    /**
     * Reset all subscription state
     */
    const resetState = useCallback(() => {
        setSubscriptionData(null);
        setIsLoggedIn(false);
        setUserType(null);
        setError(null);
        hasFetchedRef.current = false;
    }, []);

    // ==================== Main Actions ====================

    /**
     * Fetch subscription status from API
     */
    const fetchSubscriptionStatus = useCallback(async (force = false) => {
        // Skip if already fetched (unless forced)
        if (hasFetchedRef.current && !force) {
            return;
        }

        const authStatus = checkAuthStatus();

        // Not logged in - reset state
        if (!authStatus.isAuthenticated) {
            setLoading(false);
            resetState();
            return;
        }

        // Owner accounts don't need subscriptions
        if (authStatus.userType === USER_TYPE.OWNER) {
            setIsLoggedIn(true);
            setUserType(USER_TYPE.OWNER);
            setSubscriptionData({
                status: SUBSCRIPTION_STATUS.ACTIVE,
                isActive: true,
                isOwnerView: true,
                message: 'Owner account - no subscription required'
            });
            setLoading(false);
            hasFetchedRef.current = true;
            return;
        }

        // Only TRAINER users need subscription check
        if (authStatus.userType !== USER_TYPE.TRAINER) {
            setIsLoggedIn(authStatus.isAuthenticated);
            setUserType(authStatus.userType);
            setSubscriptionData(null);
            setLoading(false);
            return;
        }

        // Fetch subscription for TRAINER
        try {
            setLoading(true);
            setError(null);
            setIsLoggedIn(true);
            setUserType(USER_TYPE.TRAINER);

            const response = await axios.get(API_ENDPOINTS.STATUS);

            if (!mountedRef.current) return;

            if (response.data.error) {
                throw new Error(response.data.error);
            }

            // Process data with grace period calculation
            const processedData = calculateGracePeriod(response.data);
            setSubscriptionData(processedData);
            hasFetchedRef.current = true;

        } catch (err) {
            if (!mountedRef.current) return;

            console.error('Error fetching subscription status:', err);

            if (err.response?.status === 404) {
                // No subscription found - can start trial
                setSubscriptionData({
                    status: SUBSCRIPTION_STATUS.NONE,
                    isActive: false,
                    hasAccess: false,
                    canStartTrial: true,
                    message: 'No subscription found'
                });
            } else {
                setError(
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to fetch subscription status'
                );
                setSubscriptionData({
                    status: SUBSCRIPTION_STATUS.ERROR,
                    isActive: false,
                    hasAccess: false,
                    message: 'Failed to load subscription data'
                });
            }
            hasFetchedRef.current = true;
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    }, [checkAuthStatus, calculateGracePeriod, resetState]);

    /**
     * Force refresh subscription status
     */
    const refreshSubscriptionStatus = useCallback(() => {
        hasFetchedRef.current = false;
        return fetchSubscriptionStatus(true);
    }, [fetchSubscriptionStatus]);

    /**
     * Clear subscription data (on logout)
     */
    const clearSubscription = useCallback(() => {
        resetState();
    }, [resetState]);

    /**
     * Initialize subscription after login
     */
    const initializeAfterLogin = useCallback(async () => {
        hasFetchedRef.current = false;
        setLoading(true);
        await fetchSubscriptionStatus(true);
    }, [fetchSubscriptionStatus]);

    // ==================== Computed Values ====================

    /**
     * Check if user has access to the app
     */
    const hasAccess = useCallback(() => {
        if (!isLoggedIn) return false;
        if (userType === USER_TYPE.OWNER) return true;
        if (!subscriptionData) return false;

        return subscriptionData.hasAccess === true || subscriptionData.isActive === true;
    }, [subscriptionData, isLoggedIn, userType]);

    /**
     * Check if user is in grace period
     */
    const isInGracePeriod = useCallback(() => {
        return subscriptionData?.isInGracePeriod === true;
    }, [subscriptionData]);

    /**
     * Get grace period end date
     */
    const getGracePeriodEndDate = useCallback(() => {
        return subscriptionData?.gracePeriodEndDate || null;
    }, [subscriptionData]);

    /**
     * Check if user can start a trial
     */
    const canStartTrial = useCallback(() => {
        if (userType !== USER_TYPE.TRAINER) return false;
        if (!subscriptionData) return true;
        return (
            subscriptionData.canStartTrial === true ||
            subscriptionData.status === SUBSCRIPTION_STATUS.NONE ||
            subscriptionData.status === SUBSCRIPTION_STATUS.NOT_FOUND
        );
    }, [subscriptionData, userType]);

    /**
     * Check if user is in trial
     */
    const isTrialUser = useCallback(() => {
        return subscriptionData?.isInTrial === true;
    }, [subscriptionData]);

    /**
     * Check if app should be fully locked
     */
    const isLocked = useCallback(() => {
        if (userType === USER_TYPE.OWNER) return false;
        if (!isLoggedIn || userType !== USER_TYPE.TRAINER) return false;
        if (loading) return false;

        return !hasAccess();
    }, [isLoggedIn, userType, loading, hasAccess]);

    // ==================== Effects ====================

    // Initialize on mount
    useEffect(() => {
        mountedRef.current = true;
        fetchSubscriptionStatus();

        // Listen for storage changes (logout from another tab)
        const handleStorageChange = (e) => {
            if (e.key === 'jwt' && !e.newValue) {
                clearSubscription();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            mountedRef.current = false;
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [fetchSubscriptionStatus, clearSubscription]);

    // ==================== Context Value ====================

    const value = useMemo(() => ({
        // State
        subscriptionData,
        loading,
        error,
        isLoggedIn,
        userType,

        // Actions
        refreshSubscriptionStatus,
        clearSubscription,
        initializeAfterLogin,

        // Computed
        hasAccess,
        isInGracePeriod,
        getGracePeriodEndDate,
        canStartTrial,
        isTrialUser,
        isLocked,

        // Formatters (exposed for convenience)
        getSubscriptionStatusText,
        formatDate
    }), [
        subscriptionData,
        loading,
        error,
        isLoggedIn,
        userType,
        refreshSubscriptionStatus,
        clearSubscription,
        initializeAfterLogin,
        hasAccess,
        isInGracePeriod,
        getGracePeriodEndDate,
        canStartTrial,
        isTrialUser,
        isLocked
    ]);

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};

export default SubscriptionContext;
